// Mirrors the limits in backend/server.py. The server is the one that decides —
// these exist so a mistake is caught next to the field that caused it, instead
// of coming back as a toast after a 12MB round trip.

export const MAX_PHOTO_MB = 8;
export const MAX_PDF_MB = 40;
export const MIN_PASSWORD = 8;

// Same shapes as EMAIL_RE / PHONE_RE server-side. "a@" used to be accepted here
// and then created an account that could never receive its own code.
const EMAIL_RE = /^[^@\s]+@[^@\s.]+(?:\.[^@\s.]+)+$/;
const PHONE_RE = /^\+?\d[\d\s-]{6,18}$/;

const len = (v) => (v ?? "").trim().length;

// Each returns an error string, or null when the value is fine. Callers pass a
// list to `firstError` so a form reports one problem at a time rather than five.
export const required = (v, label) => (len(v) ? null : `${label} is required`);

export const maxLen = (v, n, label) =>
    len(v) > n ? `${label} must be under ${n} characters` : null;

export const identifier = (v) => {
    const s = (v ?? "").trim();
    if (!s) return "Enter your email or phone number";
    if (s.includes("@")) return EMAIL_RE.test(s) ? null : "That doesn't look like an email address";
    return PHONE_RE.test(s) ? null : "Enter a valid email address or phone number";
};

export const password = (v) =>
    (v ?? "").length < MIN_PASSWORD ? `Password must be at least ${MIN_PASSWORD} characters` : null;

export const personName = (v, label = "Name") =>
    required(v, label) || maxLen(v, 60, label);

// Blank is allowed — the backend stores null. A number that isn't one is not.
export const age = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 && n <= 120 ? null : "Age must be a whole number up to 120";
};

export const phone = (v, optional = true) => {
    if (!len(v)) return optional ? null : "Contact phone is required";
    return PHONE_RE.test(v.trim()) ? null : "Enter a valid phone number";
};

export const pin = (v) => (/^\d{6}$/.test((v ?? "").trim()) ? null : "PIN code must be 6 digits");

// The address a human reads before printing and posting a hardcover.
export const shipping = (a = {}) => firstError([
    personName(a.name, "Full name"),
    required(a.line1, "Address line") || maxLen(a.line1, 200, "Address line"),
    required(a.city, "City") || maxLen(a.city, 120, "City"),
    pin(a.pin),
    phone(a.phone),
]);

// A photo, before it is read into memory as a data URL. accept="image/*" is a
// filter in the file picker, not a guarantee — a file can still be dropped or
// renamed into one.
export const imageFile = (f) => {
    if (!f) return "Choose an image";
    if (!(f.type || "").startsWith("image/")) return "That file isn't an image";
    if (f.size > MAX_PHOTO_MB * 1024 * 1024) return `Images must be under ${MAX_PHOTO_MB}MB`;
    return null;
};

export const firstError = (errors) => errors.find(Boolean) || null;
